import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("k") || "";

  // States: loading | confirm | already | done | invalid | error
  const [state, setState] = useState(token ? "loading" : "invalid");
  const [emailHint, setEmailHint] = useState("");
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/unsubscribe/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 404) { setState("invalid"); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setEmailHint(data.email_hint || "");
        setState(data.already_unsubscribed ? "already" : "confirm");
      } catch (_e) {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const confirmUnsubscribe = async () => {
    setState("loading");
    setErrorText("");
    try {
      const res = await fetch(`${BACKEND}/api/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState("done");
    } catch (_e) {
      setErrorText("Something went wrong. Please reply to any Pancha Healing email and we will unsubscribe you manually.");
      setState("error");
    }
  };

  return (
    <div className="min-h-screen" data-testid="unsubscribe-page">
      <BrandHeader />
      <div className="max-w-lg mx-auto px-5 md:px-10 py-16 md:py-24">
        <div
          className="rounded-2xl p-8 md:p-10 text-center"
          style={{
            background: "linear-gradient(180deg, #FAF7F2 0%, #F3EDE0 100%)",
            border: "1px solid rgba(212, 168, 85, 0.35)",
            boxShadow: "0 20px 50px rgba(44, 10, 94, 0.08)",
          }}
        >
          {state === "loading" && (
            <div data-testid="unsub-loading">
              <p className="font-serif italic text-[#6B5E7E]">Checking your details...</p>
            </div>
          )}

          {state === "confirm" && (
            <div data-testid="unsub-confirm">
              <div className="ph-overline">Prescription Reminders</div>
              <h1 className="font-serif text-3xl md:text-4xl mt-3 text-[#2C0A5E]">Unsubscribe?</h1>
              <p className="font-serif text-[#3d2f5c] mt-5 leading-relaxed">
                You will stop receiving reminder emails about the crystal prescription you started.
              </p>
              {emailHint && (
                <p className="text-sm text-[#6B5E7E] mt-3" data-testid="unsub-email-hint">
                  Account: <strong>{emailHint}</strong>
                </p>
              )}
              <button
                type="button"
                onClick={confirmUnsubscribe}
                className="ph-btn-primary mt-8"
                data-testid="unsub-confirm-btn"
              >
                Yes, unsubscribe me
              </button>
              <p className="mt-6 text-xs text-[#6B5E7E] font-serif italic">
                Changed your mind? Simply close this page — we won&apos;t unsubscribe you unless you tap the button above.
              </p>
            </div>
          )}

          {state === "done" && (
            <div data-testid="unsub-done">
              <div className="text-4xl mb-4" aria-hidden>🌸</div>
              <h1 className="font-serif text-3xl md:text-4xl text-[#2C0A5E]">You&apos;re unsubscribed</h1>
              <p className="font-serif text-[#3d2f5c] mt-5 leading-relaxed">
                We will not send you any more prescription reminders.
              </p>
              <p className="font-serif italic text-[#6B5E7E] mt-4">
                Your prescription file remains open — if you ever want to complete it, just return to Pancha Healing.
              </p>
              <a href="/" className="ph-btn-ghost mt-8 inline-block" data-testid="unsub-back-btn">
                Back to Pancha Healing
              </a>
            </div>
          )}

          {state === "already" && (
            <div data-testid="unsub-already">
              <div className="text-4xl mb-4" aria-hidden>🌿</div>
              <h1 className="font-serif text-3xl md:text-4xl text-[#2C0A5E]">Already unsubscribed</h1>
              <p className="font-serif text-[#3d2f5c] mt-5 leading-relaxed">
                You&apos;re already off our reminder list. No further action needed.
              </p>
              <a href="/" className="ph-btn-ghost mt-8 inline-block">
                Back to Pancha Healing
              </a>
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="unsub-invalid">
              <h1 className="font-serif text-2xl md:text-3xl text-[#2C0A5E]">Link expired or invalid</h1>
              <p className="font-serif text-[#3d2f5c] mt-4 leading-relaxed">
                We couldn&apos;t verify this unsubscribe link. If you keep receiving reminders, reply to any of our emails and we&apos;ll unsubscribe you manually.
              </p>
              <a href="/" className="ph-btn-ghost mt-8 inline-block">
                Back to Pancha Healing
              </a>
            </div>
          )}

          {state === "error" && (
            <div data-testid="unsub-error">
              <h1 className="font-serif text-2xl md:text-3xl text-[#2C0A5E]">Something went wrong</h1>
              <p className="font-serif text-[#3d2f5c] mt-4 leading-relaxed">
                {errorText || "Please try again in a moment — or reply to any Pancha Healing email and we will unsubscribe you manually."}
              </p>
              <a href="/" className="ph-btn-ghost mt-8 inline-block">
                Back to Pancha Healing
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
